import * as d3 from "d3";
import { GaugeData, GaugeSegment, MarkerData, ReferenceDateValue } from "../types/gauge";
import { createRangeLevels, getBandBoundaries, hasAllBandValues } from "./range-levels";

// Calculate angles for segments
const totalAngle = 180; // 180 degrees for semicircle
const centerSegmentAngle = 18; // Minimum width for center segment
const remainingAngle = totalAngle - centerSegmentAngle;
const sideSegmentAngle = remainingAngle / 6; // Equal distribution for other segments

const GAUGE_SEGMENTS: GaugeSegment[] = [
  {
    startAngle: 180,
    endAngle: 180 + sideSegmentAngle,
    color: "#6E99C3",
    percentileRange: [0, 2.5],
  },
  {
    startAngle: 180 + sideSegmentAngle,
    endAngle: 180 + 2 * sideSegmentAngle,
    color: "#6CB9B0",
    percentileRange: [2.5, 16],
  },
  {
    startAngle: 180 + 2 * sideSegmentAngle,
    endAngle: 180 + 3 * sideSegmentAngle,
    color: "#8FAE51",
    percentileRange: [16, 45],
  },
  {
    startAngle: 180 + 3 * sideSegmentAngle,
    endAngle: 180 + 3 * sideSegmentAngle + centerSegmentAngle,
    color: "#E8BE00",
    percentileRange: [45, 55],
  },
  {
    startAngle: 180 + 3 * sideSegmentAngle + centerSegmentAngle,
    endAngle: 180 + 4 * sideSegmentAngle + centerSegmentAngle,
    color: "#DDA51B",
    percentileRange: [55, 84],
  },
  {
    startAngle: 180 + 4 * sideSegmentAngle + centerSegmentAngle,
    endAngle: 180 + 5 * sideSegmentAngle + centerSegmentAngle,
    color: "#EC5838",
    percentileRange: [84, 97.5],
  },
  {
    startAngle: 180 + 5 * sideSegmentAngle + centerSegmentAngle,
    endAngle: 360,
    color: "#DA2E83",
    percentileRange: [97.5, 100],
  },
];

// The percentile each band boundary maps to, matching GAUGE_SEGMENTS.
const BOUNDARY_PERCENTILES = [0, 2.5, 16, 45, 55, 84, 97.5, 100];

/**
 * Where a value sits on the gauge, from 0 to 100: between the band boundaries
 * built from the 9 band values (the 0, 2.5, 16, 45, 50, 55, 84, 97.5 and 100th
 * percentiles of its history) around its 10-year average. Trends places each
 * observation with its own date's band values and average the same way.
 */
export const percentileInBands = (value: number, rangesLevelSeries: number[], tenYearAverage: number): number => {
  const b = getBandBoundaries(createRangeLevels(rangesLevelSeries), tenYearAverage);
  const boundaries = [b.left4, b.left3, b.left2, b.left1, b.right1, b.right2, b.right3, b.right4];

  if (value < boundaries[0]) return 0;
  if (value > boundaries[boundaries.length - 1]) return 100;

  // A value on boundaries that coincide takes the middle of the span they
  // cover. At the average, when the center band has no width, that is 50.
  const onBoundary = boundaries.flatMap((b, i) => (b === value ? [i] : []));
  if (onBoundary.length > 0) {
    const first = BOUNDARY_PERCENTILES[onBoundary[0]];
    const last = BOUNDARY_PERCENTILES[onBoundary[onBoundary.length - 1]];
    return (first + last) / 2;
  }

  // Missing range levels leave no boundary to compare against.
  const upper = boundaries.findIndex((b) => value < b);
  if (upper < 1) return 0;

  const lower = upper - 1;
  return (
    BOUNDARY_PERCENTILES[lower] +
    ((value - boundaries[lower]) * (BOUNDARY_PERCENTILES[upper] - BOUNDARY_PERCENTILES[lower])) /
      (boundaries[upper] - boundaries[lower])
  );
};

export const calculatePercentile = (value: number, data: GaugeData): number =>
  percentileInBands(value, data.ranges_level_series, data.avg10y_value);

/**
 * Where the needle ("current") or the lag marker ("lag") sits, 0 to 100, or
 * null when there is nothing to draw. Callers don't need to know where the
 * number comes from:
 * - With the API's gauge block, its placement as sent. A null there means the
 *   API couldn't place the value (no prior value, say), so it isn't recomputed.
 * - Without it (a worker that doesn't send it yet), the app's own band math.
 *   This fallback goes away once every worker sends the block.
 */
export const percentileFor = (data: GaugeData, which: "current" | "lag"): number | null => {
  if (data.gauge) return which === "current" ? data.gauge.percentile : data.gauge.percentileLag;

  const value = which === "current" ? data.value : data.value_tm1;
  if (value === null || !hasAllBandValues(data.ranges_level_series)) return null;
  return calculatePercentile(value, data);
};

/**
 * Where a reference-date marker sits, 0 to 100, or null when it can't be
 * placed. The API's percentile when the entry carries one (null included);
 * otherwise, for an older worker or a previewed value, the app's band math.
 */
export const referencePercentileFor = (data: GaugeData, ref: ReferenceDateValue): number | null => {
  if (ref.percentile !== undefined) return ref.percentile;
  if (ref.value === null || !hasAllBandValues(data.ranges_level_series)) return null;
  return calculatePercentile(ref.value, data);
};

export const calculateMarkerAngle = (percentile: number): number => {
  // Find the segment this percentile belongs to
  const segment = GAUGE_SEGMENTS.find(
    (seg) =>
      percentile >= seg.percentileRange[0] &&
      percentile <= seg.percentileRange[1],
  );

  if (!segment) {
    return 180; // Default to start if no segment found
  }

  // Create a linear scale specific to this segment
  // Map the percentile within segment's range to the segment's angle range
  const angle = d3
    .scaleLinear()
    .domain(segment.percentileRange)
    .range([segment.startAngle, segment.endAngle])(percentile);
  return angle;
};

export const AVERAGE_PERCENTILE = 50;

/**
 * The gauge's markers. One that can't be placed is left out rather than drawn
 * at a placeholder. The average always sits at 50: the center band is
 * symmetric around it.
 */
export const getMarkerData = (data: GaugeData): MarkerData[] => {
  const currentPercentile = percentileFor(data, "current");
  const lagPercentile = percentileFor(data, "lag");

  const markers: MarkerData[] = [];
  if (currentPercentile !== null) markers.push({ value: currentPercentile, type: "current", size: "large" });
  if (lagPercentile !== null) markers.push({ value: lagPercentile, type: "lag", size: "medium" });
  markers.push({ value: AVERAGE_PERCENTILE, type: "average", size: "small" });
  return markers;
};

export const getGaugeSegments = (): GaugeSegment[] => GAUGE_SEGMENTS;
